import { SubmitNewQuote } from "@/components/SubmitNewQuote";
import {
  consumePendingDealSubmittedPayload,
  pushCustomerPortalAnalyticsEvent,
} from "@/utils";
import { useEffect, useRef, useState } from "react";

const PHONE_NUMBER = "+18664583853";
const PHONE_DISPLAY = "+1-866-458-3853";
const EMAIL = "noa@themilesmarket.com";
const WHATSAPP_CHAT_URL =
  "https://api.whatsapp.com/send?phone=15557039123";
const SUCCESS_PAGE_SESSION_KEY = "success-page-deal-data";

type DealData = {
  firstName: string | null;
  amount: number | null;
  payoutMethod: "PayPal" | "Wire" | null;
  airlineName: string | null;
  amountOfMiles: number | null;
};

const emptyDealData: DealData = {
  firstName: null,
  amount: null,
  payoutMethod: null,
  airlineName: null,
  amountOfMiles: null,
};

export function SuccessPage() {
  const hasTrackedPageOpenRef = useRef(false);
  const [dealData, setDealData] = useState<DealData>(emptyDealData);

  useEffect(() => {
    if (hasTrackedPageOpenRef.current) return;
    hasTrackedPageOpenRef.current = true;

    // Always try fresh payload first — covers new submissions and prevents
    // stale cache from a previous deal showing on a subsequent success page visit.
    const payload = consumePendingDealSubmittedPayload();
    if (payload) {
      pushCustomerPortalAnalyticsEvent({ payload });
      const data: DealData = {
        firstName: payload.first_name,
        amount: payload.amount,
        payoutMethod: payload.payout_method,
        airlineName: payload.miles_points_type,
        amountOfMiles: payload.amount_of_miles,
      };
      window.sessionStorage.setItem(
        SUCCESS_PAGE_SESSION_KEY,
        JSON.stringify(data),
      );
      setDealData(data);
      return;
    }

    // Fall back to cached data so a page refresh still shows deal info.
    const cached = window.sessionStorage.getItem(SUCCESS_PAGE_SESSION_KEY);
    if (cached) {
      try {
        setDealData(JSON.parse(cached) as DealData);
      } catch {
        // cached data is corrupt — leave empty state
      }
    }
  }, []);

  const { firstName, amount, payoutMethod, airlineName, amountOfMiles } = dealData;
  const isPayPal = payoutMethod === "PayPal";

  const hasDealDetails =
    amount != null ||
    amountOfMiles != null ||
    (airlineName != null && airlineName.trim() !== "");

  const heroHeadline = firstName
    ? `Got it, ${firstName}! We're on it.`
    : "Got it! We're on it.";

  const formattedAmount = amount
    ? `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null;

  const formattedMiles = amountOfMiles
    ? amountOfMiles.toLocaleString("en-US")
    : null;

  const card2 = isPayPal
    ? {
        title: "PayPal Payment",
        timing: "Same day, before any miles used",
        body: `Once we verify your account, payment goes out via PayPal as "Goods and Services." Look for a PayPal email — usually within 24 hours of verification.`,
      }
    : payoutMethod === "Wire"
      ? {
          title: "Booking Begins",
          timing: "Within 24 hours of verification",
          body: "We'll start booking flights for our buyers using your miles. You'll see airline confirmation emails — that's normal.",
        }
      : {
          title: "Payment & Booking",
          timing: "After verification",
          body: "We'll confirm your payout details on the verification call, then begin booking flights for our buyers.",
        };

  const card3 = isPayPal
    ? {
        title: "We Use Your Miles",
        timing: "Next 2–3 weeks",
        body: "We book flights for our buyers using the miles you sold us. You'll see airline emails — that's normal. When we're done, you change your password.",
      }
    : payoutMethod === "Wire"
      ? {
          title: "Bank Transfer",
          timing: "2–6 weeks after verification",
          body: "Once the miles have been used for buyer flights, your bank transfer goes out — free, no fees. We'll email you tracking info.",
        }
      : {
          title: "Miles Used & Paid",
          timing: "2–6 weeks after verification",
          body: "We'll use your miles for buyer flights, then send your payment. We'll keep you updated throughout.",
        };

  const steps = [
    {
      num: "01",
      title: "Verification Call",
      timing: "Next few hours",
      body: "We'll verify your miles balance and call or text you to confirm your identity. Quick 5–10 minute conversation.",
      img: "/checkout-success/verification_call..svg",
    },
    { num: "02" as const, ...card2, img: "/checkout-success/paypall_payment.svg" },
    { num: "03" as const, ...card3, img: "/checkout-success/we_use_your_miles.svg" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">

      {/* ── Hero + locked-in offer (only when we have deal payload / cache) ── */}
      {hasDealDetails && (
        <div className="bg-[#083e9e] w-full px-4 xl:px-16 pt-[8dvh] pb-0">
          <div className="max-w-[1100px] mx-auto flex flex-col items-center text-center gap-6">

            <div className="flex flex-col" style={{ gap: 20 }}>
              <h1 className="font-semibold text-white" style={{ fontSize: 60, lineHeight: "100%", letterSpacing: 0 }}>
                {heroHeadline}
              </h1>
              <p className="font-medium text-white text-center" style={{ fontSize: 22, lineHeight: "100%", letterSpacing: 0 }}>
                Here's exactly what happens next.
              </p>
            </div>

            {/* Locked-in offer card — hangs 5% below the hero */}
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 flex flex-col gap-4 text-left mt-6 relative z-10 mb-[-2.5%]">
              <div className="flex items-center justify-between">
                <p className="font-medium capitalize text-[#000000]" style={{ fontSize: 30, lineHeight: "100%", letterSpacing: 0 }}>
                  Your Locked In Offer
                </p>
                {formattedAmount && (
                  <p className="font-medium capitalize text-[#000000]" style={{ fontSize: 30, lineHeight: "100%", letterSpacing: 0 }}>{formattedAmount}</p>
                )}
              </div>
              <div className="h-px bg-[#e7ebf0]" />
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[#000000] font-medium capitalize" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>
                    <img src="/checkout-success/airplane.svg" className="w-5 h-5 flex-shrink-0" alt="" aria-hidden="true" />
                    Airline
                  </span>
                  <span className="font-medium capitalize text-[#000000]" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>
                    {airlineName ?? "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-[#000000] font-medium capitalize" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>
                    <img src="/checkout-success/miles.svg" className="w-5 h-5 flex-shrink-0" alt="" aria-hidden="true" />
                    Miles
                  </span>
                  <span className="font-medium capitalize text-[#000000]" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>
                    {formattedMiles ?? "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div
        className={`max-w-[1100px] mx-auto w-full px-4 xl:px-0 pb-14 flex flex-col gap-16 ${hasDealDetails ? "pt-[calc(2.5%+3rem)]" : "pt-8"}`}
      >

        {/* ── Progress tracker ── */}
        <div className="w-full flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0">

          {/* Step 1 — completed */}
          <div className="flex md:flex-col items-center gap-4 md:gap-3 md:flex-1 md:text-center">
            <div className="w-10 h-10 rounded-full bg-[#17D384] flex items-center justify-center flex-shrink-0 shadow-[0_0_0_4px_#17D38420]">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="font-bold capitalize" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>Successfully submitted!</p>
              <p className="font-normal text-[#17D384] mt-0.5" style={{ fontSize: 16, lineHeight: "100%", letterSpacing: 0 }}>Done</p>
            </div>
          </div>

          {/* Connector 1→2 — solid green */}
          <div className="hidden md:block flex-1 h-0.5 mx-3 rounded-full bg-[#17D384]" aria-hidden="true" />
          <div className="md:hidden ml-[19px] w-0.5 h-6 bg-[#17D384] rounded-full" aria-hidden="true" />

          {/* Step 2 — active */}
          <div className="flex md:flex-col items-center gap-4 md:gap-3 md:flex-1 md:text-center">
            <div className="relative flex-shrink-0 w-10 h-10">
              <span className="absolute inset-0 rounded-full bg-[#0566ff]/15 animate-step-ring-outer" aria-hidden="true" />
              <span className="absolute inset-0 rounded-full bg-[#0566ff]/25 animate-step-ring-inner" aria-hidden="true" />
              <div className="w-10 h-10 rounded-full bg-[#0566ff] flex items-center justify-center relative z-10 shadow-[0_4px_12px_0_rgba(5,102,255,0.35)]">
                <img src="/checkout-success/verifying.svg" className="w-5 h-5" alt="" aria-hidden="true" />
              </div>
            </div>
            <div>
              <p className="font-bold capitalize text-[#0566ff]" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>Verifying</p>
              <p className="font-normal text-gray-400 mt-0.5" style={{ fontSize: 16, lineHeight: "100%", letterSpacing: 0 }}>Right now — usually 1–3 hours</p>
            </div>
          </div>

          {/* Connector 2→3 — dashed */}
          <div className="hidden md:block flex-1 mx-3" aria-hidden="true">
            <div className="h-0.5 w-full border-t-2 border-dashed border-[#e7ebf0]" />
          </div>
          <div className="md:hidden ml-[19px] w-0.5 h-6 border-l-2 border-dashed border-[#e7ebf0]" aria-hidden="true" />

          {/* Step 3 — pending */}
          <div className="flex md:flex-col items-center gap-4 md:gap-3 md:flex-1 md:text-center">
            <div className="w-10 h-10 rounded-full border-2 border-[#e7ebf0] bg-white flex items-center justify-center flex-shrink-0">
              <img src="/checkout-success/payment_sent.svg" className="w-5 h-5" alt="" aria-hidden="true" />
            </div>
            <div>
              <p className="font-bold capitalize text-gray-400" style={{ fontSize: 20, lineHeight: "100%", letterSpacing: 0 }}>Payment sent</p>
              <p className="font-normal text-gray-400 mt-0.5" style={{ fontSize: 16, lineHeight: "100%", letterSpacing: 0 }}>
                {isPayPal ? "Same-day via PayPal" : "After verification"}
              </p>
            </div>
          </div>

        </div>

        {/* ── OTP warning ── */}
        <div className="w-full bg-[#ECFAFF] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-8 md:items-center">
          <img
            src="/checkout-success/watch_security_code.png"
            className="h-40 w-40 flex-shrink-0 object-contain mx-auto md:mx-0"
            alt=""
            aria-hidden="true"
          />
          <div className="flex flex-col gap-4">
            <h3 className="font-bold" style={{ fontSize: 44, lineHeight: "48px", letterSpacing: 0 }}>
              Watch for a security code from your airline!
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              Your airline (or credit card provider) may send you a One-Time
              Password (OTP) by text or email in the next few hours. That's
              because we're logging into your account on your behalf — exactly
              as you authorized when you submitted the form.
            </p>
            <p className="text-gray-600 leading-relaxed text-sm">
              When the code arrives, please send it to us right away. Codes
              expire in minutes, and we can't continue verification without it.
            </p>
            <p className="inline-flex w-fit text-[#181818] font-semibold uppercase bg-[#B2E3EB]" style={{ fontSize: 14, lineHeight: "15px", letterSpacing: "0.02em", borderRadius: 10, paddingTop: 16, paddingRight: 20, paddingBottom: 16, paddingLeft: 20, gap: 10 }}>
              The faster you share the code, the faster you get paid.
            </p>
          </div>
        </div>

        {/* ── Founder block ── */}
        {/* <div className="w-full bg-white rounded-2xl p-8 md:p-12 flex flex-col gap-10">
       
          <blockquote
            className="font-bold capitalize text-gray-900 text-center w-full"
            style={{ fontSize: 32, lineHeight: "100%", letterSpacing: 0 }}
          >
            "You're In Good Hands. I Personally Oversee Every Transaction At
            The Miles Market. If Anything Feels Off — Or If You've Had Second
            Thoughts — Text Or Call Me Directly. I Read Every Reply."
          </blockquote>


          <div className="flex justify-center">
            <div className="flex items-center gap-5">
       
              <div className="w-20 h-20 rounded-full bg-[#407BFF] flex items-center justify-center flex-shrink-0">
                <span className="text-white text-3xl font-bold" aria-hidden="true">N</span>
              </div>

            
              <div className="flex flex-col gap-2">
                <div>
                  <p className="font-bold text-gray-900 text-lg leading-tight">Noa</p>
                  <p className="font-normal capitalize text-[#181818]" style={{ fontSize: 16, lineHeight: "25px", letterSpacing: 0 }}>Founder of The Miles Market</p>
                </div>
                <div className="flex flex-wrap gap-3 items-center">
  
                  <a
                    href={`mailto:${EMAIL}`}
                    className="flex items-center gap-2 bg-[#0566ff] text-white text-sm font-medium rounded-full px-4 py-2 hover:bg-[#0452d4] transition-colors"
                  >
                    <img src="/checkout-success/send.svg" className="w-3.5 h-3.5 brightness-0 invert" alt="" aria-hidden="true" />
                    {EMAIL}
                  </a>
               
                  <a
                    href={`tel:${PHONE_NUMBER}`}
                    className="flex items-center gap-2 bg-[#EBF8FB] text-[#181818] rounded-full px-4 py-2 hover:bg-[#d6f2f8] transition-colors"
                    style={{ fontSize: 16, fontWeight: 500, lineHeight: 0, letterSpacing: 0 }}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {PHONE_DISPLAY}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        </div>

      {/* ── How does it work? — full viewport width ── */}
      <div className="w-full bg-[#EAFCFF] py-14">
        <div className="max-w-[1100px] mx-auto px-4 xl:px-0 flex flex-col gap-24 md:gap-12">
          <div className="flex flex-col gap-3 text-center">
            <h3 className="text-4xl font-bold">How does it work?</h3>
            {/*<p className="text-gray-500 text-base max-w-lg mx-auto">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum
              vel ullamcorper nunc, non viverra nunc.
            </p>*/}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-20 md:gap-6 pb-6">
            {steps.map(({ num, title, body, img }) => (
              <div key={num} className="flex flex-col items-center">
                <img
                  src={img}
                  className="h-44 w-auto object-contain relative z-10 mb-[-56px]"
                  alt=""
                  aria-hidden="true"
                />
                <div className="relative bg-white rounded-2xl w-full pt-20 pb-10 px-6 flex flex-col items-center gap-4 text-center">
                  <p className="font-bold" style={{ fontSize: 24, lineHeight: "24px", letterSpacing: 0 }}>{title}</p>
                  <p className="font-normal text-[#181818]" style={{ fontSize: 18, lineHeight: "26px", letterSpacing: 0 }}>{body}</p>
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-[#1C2FA0] flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{num}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto w-full px-4 xl:px-0 pb-14 flex flex-col gap-16">

        {/* ── Easiest ways to reach us ── */}
        <div className="w-full flex flex-col md:flex-row gap-8 items-start py-[100px]">
          <h3 className="font-semibold md:w-56 flex-shrink-0" style={{ fontSize: 48, lineHeight: "100%", letterSpacing: 0 }}>Easiest ways to reach us</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 flex-1">
            {[
              {
                icon: <img src="/checkout-success/chat_buble.svg" className="w-10 h-10" alt="" aria-hidden="true" />,
                title: "Text us your number",
                desc: "Most sellers prefer texting — faster than phone tag.",
                href: `mailto:${EMAIL}?subject=My%20WhatsApp%2FSMS%20number`,
                label: "Reply With Your Number",
              },
              {
                icon: (
                  <svg className="w-10 h-10 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                ),
                title: "WhatsApp",
                desc: "Message us directly — quick replies during business hours.",
                href: WHATSAPP_CHAT_URL,
                label: "Start chat",
                external: true,
              },
              {
                icon: <img src="/checkout-success/phone.svg" className="w-10 h-10" alt="" aria-hidden="true" />,
                title: "Call us directly",
                desc: "A real person picks up.",
                href: `tel:${PHONE_NUMBER}`,
                label: PHONE_DISPLAY,
              },
              {
                icon: <img src="/checkout-success/mail.svg" className="w-10 h-10" alt="" aria-hidden="true" />,
                title: "Email us",
                desc: "We respond within an hour during business times.",
                href: `mailto:${EMAIL}`,
                label: EMAIL,
              },
            ].map(({ icon, title, desc, href, label, external }, i) => (
              <div
                key={title}
                className="flex flex-col items-center text-center"
                style={{ padding: 30, gap: 30, ...(i === 0 && { background: "#fff", borderRadius: 12, boxShadow: "0px 10px 40px 0px #0000000F" }) }}
              >
                <div className="mb-1">{icon}</div>
                <p className="font-bold text-[#181818]" style={{ fontSize: 24, lineHeight: "100%", letterSpacing: 0 }}>{title}</p>
                <p className="font-normal text-gray-400" style={{ fontSize: 16, lineHeight: "100%", letterSpacing: 0 }}>{desc}</p>
                <a
                  href={href}
                  {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="font-semibold capitalize text-[#0566ff] underline decoration-solid mt-auto"
                  style={{ fontSize: 16, lineHeight: "100%", letterSpacing: 0 }}
                >
                  {label}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* ── Trust band ── */}
        {/* <div className="w-full bg-[#ECFAFF] rounded-xl px-8 py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-2xl font-semibold">Trusted by 50,000+ sellers since 2017</p>
          <p className="text-gray-500">
            <span className="text-[#00B67A]">★</span> 4.7 on Trustpilot · 1,139 reviews
          </p>
          <a
            href="https://www.trustpilot.com/review/themilesmarket.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-[#0566ff] hover:underline"
          >
            Read reviews →
          </a>
          <p className="text-sm text-gray-500 pt-2 border-t border-[#e7ebf0] w-full mt-2">
            Questions? Call{" "}
            <a href={`tel:${PHONE_NUMBER}`} className="font-semibold text-gray-700 hover:underline">
              {PHONE_DISPLAY} (ext. 1)
            </a>{" "}
            — A real person picks up.
          </p>
        </div> */}

        <SubmitNewQuote />
      </div>
    </div>
  );
}
